import React from 'react';
import RepProfileSectionHeader from './RepProfileSectionHeader';
import RepProfileSkillsScoredBySelector from './RepProfileSkillsScoredBySelector';
import TrendChartContainer from 'components/manager-dashboard/strengths-and-weaknesses/TrendChartContainer';
import './RepProfileSkills.styl';
import LoadingOverlay from 'components/pure/utils/LoadingOverlay';
import Link from 'react-router/lib/Link';
import listAsString from 'utils/listAsString';
import {planEnd} from 'records/CoachingPlan';
import buildUrl from 'components/dashboard/buildUrl';
import {GroupingTrendChartLegend} from 'components/pure/charts/trend/GroupingTrendChartLegend';
import {RepProfileTab} from '@store/coaching-plans';
import AuthStore from 'stores/AuthStore';
import Button from '@material-ui/core/Button';

function formatDate(aMoment) {
  return aMoment.format('MM/DD/Y');
}

/**
 * Allow the user to see a trend of skills over time, with overlaid coaching data
 *
 * Note that this page uses the date logic from the other pages but deviates a bit.
 *
 * 1. We will always show 3 months of data.  If you select one of the date ranges
 * and we're under 3 months, we'll look back to 3 months from the end date.
 *
 * 2. Selecting a plan to view in more detail modifies the chart dates to +/- 1
 * month of the plan dates.  We will always stop at yesterday vs going into the future,
 * however.
 *
 */
export default class RepProfileSkills extends React.PureComponent {
  render() {
    const {
      rep,
      SkillStore,
      areCoachingPlansLoading,
      coachingPlans,
      headerStartDate,
      headerEndDate,
      isSummary,
    } = this.props;

    return (
      <span>
        {!isSummary && (
          <section className="rep-profile-activities main-section rep-profile-skills">
            <RepProfileSectionHeader
              rep={rep}
              showToggle={false}
              showTeamIcon={false}
              title={rep ? rep.firstName + "'s Skills" : ''}
              startDate={headerStartDate}
              endDate={headerEndDate}
              location={this.props.location}
            />
            <RepProfileSkillsScoredBySelector
              onSelectScoredByType={scoredBy => {
                this.props.onSelectScoredByType(scoredBy);
              }}
              value={this.props.selectedScoredByType}
            />
            {!SkillStore.teamTrendsLoading &&
            SkillStore.teamTrends.size === 0 &&
            coachingPlans.size === 0 ? (
              <div className="placeholder">
                <p>
                  There are no scores for your selections. Please score some calls to see results
                  here.
                </p>
                <img
                  alt="Screen by Rep"
                  src={require('images/manager-dashboard/screen-s-w-by-rep@1x.png')}
                />
              </div>
            ) : SkillStore.teamTrendsLoading ? null : (
              <TrendChartContainer
                chartLabel=""
                margins={{
                  left: 0,
                  top: 10,
                  right: 0,
                  bottom: 25,
                }}
                startDate={headerStartDate}
                endDate={headerEndDate}
                filterData={data => {
                  // api returns data outside our selected window (intentionally!)
                  return data
                    .map(line => {
                      return {
                        ...line,
                        data: line.data,
                      };
                    })
                    .filter(line => line.data.size > 0);
                }}
                legend={GroupingTrendChartLegend}
              />
            )}
          </section>
        )}
        <CoachingPlanTable
          {...this.props}
          areCoachingPlansLoading={areCoachingPlansLoading}
          coachingPlans={coachingPlans}
          rep={rep}
        />
      </span>
    );
  }
}

const formatScore = score => {
  if (score == null) return 'N/A';
  return score + '%';
};

const getScoreDetailFrom = (plan, scoreType) => {
  return plan.reviewScores.size === 1
    ? formatScore(plan.reviewScores.get(0)[scoreType])
    : plan.reviewScores.map(rs => (
        <div key={rs.contentDetailId}>
          <strong>{rs.skillName}:&nbsp;</strong>
          {formatScore(rs[scoreType])}
        </div>
      ));
};

const CoachingPlanTable = ({
  areCoachingPlansLoading,
  coachingPlans: coachingPlansIn,
  rep,
  teamId,
  coachId,
  coach,
  managerId,
  adminId,
  plansLimit,
  showMoreLink,
  isSummary,
}) => {
  // if a coach in dashV2 gets here (currently they won't, but may soon), the link will go to their coaching plans page
  // XXX don't think this buildUrl need to pop/push to dash history atm
  const link =
    managerId || coachId
      ? buildUrl({
          coachId,
          managerId,
          adminId,
        }) + '/coaching-plans'
      : '/metrics/profile';
  const coachingPlansInSorted = coachingPlansIn.sort((l, r) =>
    l.start.isBefore(r.start) ? 1 : -1
  );

  const coachingPlans =
    plansLimit == null ? coachingPlansInSorted : coachingPlansInSorted.slice(0, plansLimit);
  function getQuery(plan) {
    return {
      planId: plan.id,
      userId: rep.id,
      teamId: teamId,
      tab: RepProfileTab.COACHING_PLANS,
    };
  }
  const sessionUser = AuthStore.getState().user;
  const forCoachHeaderPart =
    isSummary && coach ? (sessionUser.id === coachId ? 'For You ' : `For ${coach.firstName} `) : '';
  return areCoachingPlansLoading && !isSummary ? (
    <LoadingOverlay />
  ) : coachingPlans.size === 0 ? null : (
    <section className="rep-profile-activities main-section plan-area">
      <div>
        <h2 className="plan-table-header">Coaching Plans {forCoachHeaderPart}(12 Months)</h2>
        <table className="plans-table">
          <thead>
            <tr>
              <td>From</td>
              <td>To</td>
              <td>Coach</td>
              <td>Skill</td>
              <td>Starting Score</td>
              <td>Ending Score</td>
              <td>&nbsp;</td>
            </tr>
          </thead>
          <tbody>
            {coachingPlans
              .filter(plan => plan.completed || !plan.unscheduled())
              .map(plan => {
                return (
                  <tr key={plan.id}>
                    <td className="plan-date">{formatDate(plan.start)}</td>
                    <td className="plan-date">
                      {planEnd(
                        plan.end,
                        plan.completed,
                        plan.reviews.filter(r => r.status === 'COMPLETED').size
                      )}
                    </td>
                    <td>{plan.coach.info.getFullName()}</td>
                    <td className="skill-name">
                      {plan.livecoaching
                        ? 'Live Coaching'
                        : listAsString(plan.contentDetails.map(cd => cd.skillName || cd.custom))}
                    </td>
                    <td>{!plan.livecoaching ? getScoreDetailFrom(plan, 'startScore') : 'N/A'}</td>
                    <td>{plan.completed ? getScoreDetailFrom(plan, 'endScore') : 'N/A'}</td>
                    <td className="actions">
                      <Button
                        component={Link}
                        size="small"
                        to={{
                          pathname: link,
                          query: getQuery(plan),
                        }}>
                        View Plan
                      </Button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      {showMoreLink && (
        <div className="show-more-link">
          <Button component={Link} variant="text" to={showMoreLink}>
            Show More
          </Button>
        </div>
      )}
    </section>
  );
};
